import { useEffect, useState } from 'react';
import type { UpdateReferralRatesContext } from '@scale/genai-compass-shared/cards/update_referral_rates';
import { useCompassCard } from '@scale/compass-ui/store';
import { columnsFromRecord } from '@scale/compass-shared/utils';
import { getWorkerIdColumn } from 'react-scale/corp/views/DataCompassCardsV2/action/UpdateMetadataCard/utils';
import {
  MAX_RATE_AMOUNT,
  MIN_RATE_AMOUNT,
} from 'react-scale/corp/views/DataCompassCardsV2/action/UpdateReferralRatesCard/constants';
import { addDays } from 'date-fns';
import { MAX_EXPIRES_IN_DAYS } from './constants';

const useUpdateReferralRatesCard = (index: number) => {
  const minExpireDate = new Date();
  const maxExpireDate = addDays(minExpireDate, MAX_EXPIRES_IN_DAYS);

  const { card, updateCardContext, updateCardCanSubmit } =
    useCompassCard<UpdateReferralRatesContext>(index);
  const { retrievedOutput } = useCompassCard(index - 1);
  const context = card.defaultContext;
  const data = retrievedOutput?.response?.data ?? [];

  // Get columns from the previous card's output
  const availableColumns = columnsFromRecord(retrievedOutput?.response?.data) || [];
  const workerIdColumn = getWorkerIdColumn(retrievedOutput);
  const [numWorkers, setNumWorkers] = useState(0);
  const [isValidRateInput, setIsValidRateInput] = useState(true);

  const setSelectedColumnIdWithWorkerIds = (selectedColumnIdWithWorkerIds: string) => {
    updateCardContext({ ...context, selectedColumnIdWithWorkerIds });
    if (!selectedColumnIdWithWorkerIds) {
      setNumWorkers(0);
      return;
    }
    const workerRows = data.filter(row => row[selectedColumnIdWithWorkerIds]);
    setNumWorkers(workerRows.length);
  };

  const setRateAmountDollars = (rateAmountStr: string) => {
    const rateAmountDollars = rateAmountStr ? parseInt(rateAmountStr, 10) : 0;
    if (
      !/^\d*$/.test(rateAmountStr) ||
      rateAmountDollars < MIN_RATE_AMOUNT ||
      rateAmountDollars > MAX_RATE_AMOUNT
    ) {
      setIsValidRateInput(false);
      return;
    }
    setIsValidRateInput(true);
    updateCardContext({ ...context, rateAmountDollars });
  };

  const setExpireDate = (expireDate: Date) => {
    updateCardContext({ ...context, expireDate });
  };

  const setSendComms = (sendComms: boolean) => {
    updateCardContext({ ...context, sendComms });
  };

  useEffect(() => {
    if (workerIdColumn) {
      setSelectedColumnIdWithWorkerIds(workerIdColumn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerIdColumn]);

  useEffect(() => {
    if (
      numWorkers > 0 &&
      isValidRateInput &&
      context.selectedColumnIdWithWorkerIds &&
      Number.isInteger(context.rateAmountDollars) &&
      context.rateAmountDollars >= MIN_RATE_AMOUNT &&
      context.rateAmountDollars <= MAX_RATE_AMOUNT &&
      context.expireDate
    ) {
      updateCardCanSubmit(true);
    } else {
      updateCardCanSubmit(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    context.selectedColumnIdWithWorkerIds,
    context.rateAmountDollars,
    context.expireDate,
    isValidRateInput,
    numWorkers,
  ]);

  return {
    minExpireDate,
    maxExpireDate,
    isValidRateInput,
    selectedColumnIdWithWorkerIds: context.selectedColumnIdWithWorkerIds,
    setSelectedColumnIdWithWorkerIds,
    setRateAmountDollars,
    expireDate: context.expireDate,
    setExpireDate,
    sendComms: context.sendComms,
    setSendComms,
    availableColumns,
    numWorkers,
  };
};

export default useUpdateReferralRatesCard;
